import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Alert,
  Button,
  Divider,
  Skeleton,
} from "@mui/material";
import { MdRefresh, MdArrowUpward, MdArrowDownward, MdOpenInNew } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { Surface, SectionTitle, Kpi, StatusChip, DataTable } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

/**
 * What this pharmacy is owed, and every movement behind it.
 *
 * Everything here comes from the ledger — the same service that answers the
 * pharmacy's own app at /pharmacy/wallet/:id — so the two screens cannot
 * disagree. Nothing on this tab reads `PharmacyOrders.totalBalance`, the
 * pre-ledger counter that credited on order ACCEPT at the gross price and was
 * never debited on payout; it was what made this console report balances the
 * pharmacy could not actually withdraw.
 *
 * Read-only by design. Money moves through the payouts screen, which locks,
 * releases and settles against the same ledger and records a posting for every
 * step. A balance edit here would create money with no transfer behind it.
 */

const when = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/**
 * The machine-readable codes the withdrawal gate returns, in the words an
 * admin would use. Falling back to the raw code is deliberate — a new gate
 * should show up as something unfamiliar rather than be silently swallowed.
 */
const BLOCKED_REASONS = {
  REQUEST_IN_PROGRESS: {
    label: "A withdrawal is already in progress",
    detail: "Only one request can be open at a time. It is shown below.",
  },
  NO_BANK_DETAILS: {
    label: "No bank details on file",
    detail: "Add them on the Bank tab before any payout can be raised.",
  },
  BANK_UNVERIFIED: {
    label: "Bank account not verified",
    detail: "The account must be proved by penny drop, not just typed in.",
  },
  BANK_VERIFICATION_PENDING: {
    label: "Bank verification in progress",
    detail: "Waiting on the penny drop to return the registered name.",
  },
  BANK_UNDER_REVIEW: {
    label: "Bank name match needs review",
    detail: "The name the bank returned is close but not identical. A human decides.",
  },
  BANK_VERIFICATION_FAILED: {
    label: "Bank verification failed",
    detail: "The account could not be proved. Correct the details and retry.",
  },
  BANK_COOLDOWN: {
    label: "Bank details recently changed",
    detail: "Withdrawals are held for a cooling-off window after an account change.",
  },
  BELOW_MINIMUM: {
    label: "Balance is below the minimum",
    detail: "There is nothing wrong — there simply is not enough to withdraw yet.",
  },
  PHARMACY_NOT_FOUND: { label: "Pharmacy not found", detail: null },
};

/** Ledger reasons, as a person would say them. */
const REASON_TONES = {
  order_fulfilment: T.success,
  opening_balance: T.info,
  order_reversal: T.error,
  payout_lock: T.warning,
  payout_release: T.info,
  payout_settled: T.primary,
  manual_credit: T.success,
  manual_debit: T.error,
  tds_withholding: T.muted,
};

export default function WalletTab({ pharmacyId }) {
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [statement, setStatement] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.getWallet(pharmacyId, { limit: 25 });
      setWallet(data);
      setStatement(data.statement || []);
      setCursor(data.nextCursor || null);
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not load the wallet."
      );
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  const loadPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    try {
      const data = await api.getPayoutRequests(pharmacyId, {
        status: "all",
        limit: 25,
      });
      setPayouts(data?.list || []);
    } catch {
      // The statement above is the authoritative record; a failure to list
      // requests must not take the whole tab down with it.
      setPayouts([]);
    } finally {
      setPayoutsLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    load();
    loadPayouts();
  }, [load, loadPayouts]);

  /** Append the next page. Cursor-based, so nothing shifts under a new posting. */
  const loadMore = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const data = await api.getWallet(pharmacyId, { limit: 25, cursor });
      setStatement((rows) => [...rows, ...(data.statement || [])]);
      setCursor(data.nextCursor || null);
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load more movements.");
    } finally {
      setLoadingMore(false);
    }
  };

  const blocked = wallet?.blockedReason
    ? BLOCKED_REASONS[wallet.blockedReason] || {
        label: wallet.blockedReason,
        detail: null,
      }
    : null;

  const statementColumns = [
    {
      field: "createdAt",
      headerName: "When",
      width: 175,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.6 }}>
          <Typography sx={{ fontSize: "0.8rem", color: T.ink }}>
            {when(row.createdAt)}
          </Typography>
          <Typography sx={{ fontSize: "0.66rem", color: T.subtle, fontFamily: "monospace" }}>
            #{row.seq}
          </Typography>
        </Box>
      ),
    },
    {
      field: "label",
      headerName: "What happened",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.6, minWidth: 0 }}>
          <StatusChip
            tone={REASON_TONES[row.reason] || T.muted}
            label={row.label || row.reason}
          />
          {row.note && (
            <Typography sx={{ fontSize: "0.7rem", color: T.subtle, mt: 0.4 }}>
              {row.note}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "reference",
      headerName: "Reference",
      width: 175,
      sortable: false,
      renderCell: ({ row }) =>
        row.orderId || row.payoutRequestId ? (
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontFamily: "monospace",
              color: T.muted,
              wordBreak: "break-all",
            }}
          >
            {row.orderId || row.payoutRequestId}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: "0.72rem", color: T.subtle }}>—</Typography>
        ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => {
        const credit = row.direction === "credit";
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {credit ? (
              <MdArrowDownward size={14} color={T.success} />
            ) : (
              <MdArrowUpward size={14} color={T.error} />
            )}
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: credit ? T.success : T.error,
              }}
            >
              {credit ? "+" : "−"}
              {row.amount?.display}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "balanceAfter",
      headerName: "Balance after",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: "0.82rem",
            color: T.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {row.balanceAfter?.display}
        </Typography>
      ),
    },
  ];

  const payoutColumns = [
    {
      field: "requestId",
      headerName: "Request",
      flex: 1,
      minWidth: 175,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.6, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: T.ink }}>
            {row.requestId}
          </Typography>
          <Typography sx={{ fontSize: "0.68rem", color: T.subtle }}>
            {when(row.requestedAt || row.createdAt)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: T.ink,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {row.amount?.display}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => <StatusChip status={row.status} />,
    },
    {
      field: "utr",
      headerName: "Bank reference",
      width: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontFamily: "monospace",
            color: row.razorpay?.utr ? T.ink : T.subtle,
            wordBreak: "break-all",
          }}
        >
          {/* The UTR is what a pharmacy quotes to its bank when it says the
              money never arrived. */}
          {row.razorpay?.utr || (row.status === "paid" ? "no UTR recorded" : "—")}
        </Typography>
      ),
    },
    {
      field: "reason",
      headerName: "Note",
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "0.74rem", color: T.muted }}>
          {row.rejectionReason || row.razorpay?.failureReason || "—"}
        </Typography>
      ),
    },
  ];

  if (loading && !wallet) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={110} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }

  if (error && !wallet) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {/* ── Balances ───────────────────────────────────────────────────── */}
      <Surface>
        <Grid container>
          {[
            {
              label: "Withdrawable",
              value: wallet?.balance?.display,
              sub: wallet?.canWithdraw
                ? "can be withdrawn now"
                : "withdrawal blocked — see below",
              tone: T.success,
            },
            {
              label: "Locked",
              value: wallet?.locked?.display,
              sub: wallet?.openRequest
                ? `held for ${wallet.openRequest.requestId}`
                : "no open request",
              tone: wallet?.locked?.paise ? T.warning : T.muted,
            },
            {
              label: "Lifetime earned",
              value: wallet?.lifetimeEarned?.display,
              sub: "credited on delivery",
              tone: T.info,
            },
            {
              label: "Lifetime paid out",
              value: wallet?.lifetimeWithdrawn?.display,
              sub: "settled to bank",
              tone: T.primary,
            },
          ].map((kpi, index) => (
            <Grid
              item
              xs={6}
              md={3}
              key={kpi.label}
              sx={{
                borderRight: { md: index < 3 ? `1px solid ${T.borderSoft}` : "none" },
                borderBottom: { xs: `1px solid ${T.borderSoft}`, md: "none" },
              }}
            >
              <Kpi
                label={kpi.label}
                value={kpi.value ?? "—"}
                sub={kpi.sub}
                tone={kpi.tone}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>
      </Surface>

      {/* Withdrawable and Locked are two different ledger accounts and the sum
          is not obvious from four tiles, so it is stated rather than implied. */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 0.5 }}
      >
        <Typography sx={{ fontSize: "0.78rem", color: T.subtle }}>
          Total in wallet <b style={{ color: T.ink }}>{wallet?.total?.display}</b> ·
          minimum withdrawal {wallet?.minWithdrawal?.display}
        </Typography>
        <Button
          size="small"
          startIcon={<MdRefresh />}
          onClick={() => {
            load();
            loadPayouts();
          }}
          sx={{ textTransform: "none", color: T.primary, ml: "auto" }}
        >
          Refresh
        </Button>
        <Button
          size="small"
          startIcon={<MdOpenInNew size={15} />}
          onClick={() => navigate("/admin/pharmacy-payout-req")}
          sx={{ textTransform: "none", color: T.secondary }}
        >
          Payouts console
        </Button>
      </Stack>

      {/* ── Why a withdrawal can't be raised ───────────────────────────── */}
      {blocked && (
        <Surface
          sx={{
            p: 2,
            borderColor: tint(T.warning, 0.4),
            bgcolor: tint(T.warning, 0.05),
          }}
        >
          <Typography sx={{ fontWeight: 700, color: T.ink, fontSize: "0.9rem" }}>
            {blocked.label}
          </Typography>
          {blocked.detail && (
            <Typography sx={{ fontSize: "0.83rem", color: T.muted, mt: 0.4 }}>
              {blocked.detail}
            </Typography>
          )}
        </Surface>
      )}

      {wallet?.openRequest && (
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.85rem" }}>
          <b>{wallet.openRequest.amount?.display}</b> is {wallet.openRequest.status}{" "}
          under {wallet.openRequest.requestId}, raised{" "}
          {when(wallet.openRequest.requestedAt)}. That money sits in Locked and is
          not withdrawable again until the request settles or is rejected.
        </Alert>
      )}

      {error && wallet && <Alert severity="error">{error}</Alert>}

      {/* ── Statement ──────────────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 1.5, md: 2 } }}>
        <SectionTitle>Statement</SectionTitle>
        <Divider sx={{ mb: 1.5 }} />

        <DataTable
          rows={statement}
          columns={statementColumns}
          loading={loading}
          emptyMessage="Nothing has moved through this wallet yet — the first credit lands when an order is delivered and paid."
          // The ledger's per-account sequence is already a unique, total order.
          getRowId={(row) => row.seq}
          height={statement.length ? 420 : 220}
        />

        {hasMore && (
          <Stack alignItems="center" sx={{ pt: 1.5 }}>
            <Button
              size="small"
              onClick={loadMore}
              disabled={loadingMore}
              sx={{ textTransform: "none", color: T.primary }}
            >
              {loadingMore ? "Loading…" : "Load older movements"}
            </Button>
          </Stack>
        )}

        <Typography sx={{ fontSize: "0.74rem", color: T.subtle, mt: 1.2, px: 1 }}>
          Read-only. Every row is a ledger posting with the balance it produced —
          money moves through the payouts console, never from this screen.
        </Typography>
      </Surface>

      {/* ── Withdrawal requests ────────────────────────────────────────── */}
      <Surface sx={{ p: { xs: 1.5, md: 2 } }}>
        <SectionTitle>Withdrawal requests</SectionTitle>
        <Divider sx={{ mb: 1.5 }} />
        <DataTable
          rows={payouts}
          columns={payoutColumns}
          loading={payoutsLoading}
          emptyMessage="This pharmacy has never raised a withdrawal."
          getRowId={(row) => row.requestId}
          height={payouts.length ? 360 : 200}
        />
      </Surface>
    </Stack>
  );
}
